import { useContext, useState, useEffect } from 'react';
import { Icon, IconButton, Typography, Box, Stack } from '@ace-clublabs/shared-component-library';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { ProductType } from '../../application/models/NewJoinSessionData';
import { getProductPrice, eligibleForERS } from '../../utils/helpers';
import { getClubContent, ClubContentName, FEE_AMOUNT_PLACEHOLDER } from '../../utils/getClubContent';
import { ClubCode } from '../../utils/constants';
import { AddOn } from './AddOn';
import { AddedInfoCard } from './AddedInfoCard';

export interface ERSAddOnPros {
  setTooltipInfo: Function;
  setTooltipContent: Function;
  setDeleteInfo: Function;
  selectedERS: boolean;
}

export const ERSAddOn: (props: ERSAddOnPros) => JSX.Element = ({
  setTooltipInfo,
  setTooltipContent,
  setDeleteInfo,
  selectedERS,
}) => {
  const { membershipInfo, sessionData, updateProducts, isPriceCartLoading } = useContext(MembershipAppStateContext);
  const [isUpdating, setIsUpdating] = useState(false);
  const [displaySelected, setDisplaySelected] = useState(selectedERS);

  useEffect(() => {
    if (!isPriceCartLoading && isUpdating) {
      setDisplaySelected(selectedERS);
      setIsUpdating(false);
    }
  }, [isPriceCartLoading]);

  const ersFee = membershipInfo?.membershipCost?.fees?.sameDayServiceFee;
  const ERSNotEligibleContent = (
    getClubContent(ClubContentName.ERSNotEligibleContent, sessionData.clubCode!) as string
  ).replaceAll(FEE_AMOUNT_PLACEHOLDER, `${ersFee}`);
  return (
    <>
      <Box className="benefit-add-ons">
        <Stack direction="row" alignItems="center" gap="0.5rem">
          <Icon
            iconName="truck-duotone"
            className="benefit-add-ons-icon"
            sx={{ color: '#0C7A93', fontSize: '1.5rem' }}
          />
          <Typography className="benefit-add-ons-title" variant="b1Medium">
            Immediate Roadside Assistance
          </Typography>
        </Stack>

        {eligibleForERS(membershipInfo, sessionData) ? (
          <IconButton
            aria-label="Need roadside assistance now?"
            className="add-immediate-roadside-info-icon"
            onClick={() => {
              setTooltipInfo({
                tooltipTitle: 'Need roadside assistance now?',
              });
              setTooltipContent(
                `A non-refundable $${ersFee} service fee applies to each Roadside Assistance request made within the first 3 days of membership, including for vehicle breakdowns that occurred before you joined.\n\nThese services do not count toward your 4 included roadside assistance calls.`
              );
            }}
          >
            <Icon iconName="circle-info-regular" sx={{ color: '#707070' }} />
          </IconButton>
        ) : (
          <></>
        )}
      </Box>
      {eligibleForERS(membershipInfo, sessionData) ? (
        <>
          {sessionData.clubCode !== ClubCode.calif ? (
            <AddedInfoCard
              className="ers-info"
              content={`A $${ersFee} non-refundable service fee applies each time you use Roadside Assistance for the first 3 days of your new membership. This service does not count toward your 4 included roadside assistance calls per membership year.`}
            />
          ) : displaySelected ? (
            <AddedInfoCard
              title="Added"
              isUpdating={isUpdating}
              content={`Covers one roadside assistance request.`}
              className="ers-added-card"
              originalPrice={ersFee}
              priceSuffix="once"
              deleteButtonOnClick={() => {
                setDeleteInfo({
                  title: 'Are you sure you want to remove same day roadside service?',
                  warningContent: `If you have a vehicle disablement before you join, that disablement will not be covered after you join as one of your four allowable service calls per membership year. If you would like such service, you may request service for an additional $${ersFee} non-refundable service fee.`,
                  confirmRemove: () => {
                    setIsUpdating(true);
                    updateProducts('remove', ProductType.ERS);
                  },
                });
              }}
            />
          ) : (
            <AddOn
              onAdd={() => {
                setIsUpdating(true);
                updateProducts('add', ProductType.ERS);
              }}
              text="Get roadside help now"
              price={getProductPrice(membershipInfo, ProductType.ERS, sessionData)}
              icon="plus-regular"
              type={ProductType.ERS}
              priceSuffix="once"
              isPriceCartLoading={isPriceCartLoading}
              isUpdating={isUpdating}
            />
          )}
        </>
      ) : (
        <>
          <AddedInfoCard className="ers-not-eligible-info" content={ERSNotEligibleContent} />
        </>
      )}
    </>
  );
};
